import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as programTestsService from "../services/programTests";
import * as programsService from "../services/programs";
import { loadPlacementLead } from "./PlacementTestStart";
import "./PlacementTestLevelSelect.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function uniqueLevelsFromTests(tests) {
  const seen = new Set();
  const levels = [];
  asArray(tests).forEach((t) => {
    const grade = String(t?.level_grade ?? "").trim();
    if (!grade || seen.has(grade)) return;
    seen.add(grade);
    levels.push(grade);
  });
  // Try to keep a friendly order (CP first, then numeric-ish, then the rest).
  const score = (g) => {
    const lower = String(g).toLowerCase();
    if (lower === "cp") return -100;
    const num = Number(lower.replace(/[^0-9]/g, ""));
    if (!Number.isNaN(num) && num) return num;
    return 999;
  };
  return levels.sort((a, b) => score(a) - score(b));
}

export default function PlacementTestLevelSelect() {
  const { accessToken } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const tFn = useCallback(
    (key, options) => {
      try {
        return t(key, options);
      } catch {
        return options?.defaultValue || key;
      }
    },
    [t],
  );

  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );

  const programSlug = String(query.get("program") || "").trim();

  const lead = useMemo(
    () => (programSlug ? loadPlacementLead(programSlug) : null),
    [programSlug],
  );

  const [programLoading, setProgramLoading] = useState(false);
  const [programError, setProgramError] = useState("");
  const [programOptions, setProgramOptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tests, setTests] = useState([]);

  // Always loaded: the level step also shows the chosen program's name.
  useEffect(() => {
    let ignore = false;

    async function loadProgramsWithTests() {
      setProgramLoading(true);
      setProgramError("");
      try {
        const testsPayload = await programTestsService.fetchProgramTests();
        const publishedTests = asArray(testsPayload?.tests);
        const programSlugs = new Set();
        const programIds = new Set();
        publishedTests.forEach((t) => {
          const slug = String(t?.program_slug ?? "").trim();
          if (slug) programSlugs.add(slug);
          const id = t?.program_id;
          if (id != null && String(id).trim()) programIds.add(String(id));
        });

        let programs = [];
        try {
          const programsPayload = await programsService.fetchPrograms();
          programs = asArray(programsPayload?.programs);
        } catch {
          programs = [];
        }

        const filteredPrograms = programs
          .filter((p) => {
            const slug = String(p?.slug ?? "").trim();
            const id = p?.id != null ? String(p.id) : "";
            return Boolean(
              (slug && programSlugs.has(slug)) || (id && programIds.has(id)),
            );
          })
          .map((p) => ({
            slug: String(p?.slug ?? p?.id ?? "").trim(),
            title: String(p?.title ?? p?.name ?? p?.slug ?? "").trim(),
            description: String(p?.description ?? "").trim(),
            icon: String(p?.icon_class ?? "").trim(),
            accent: String(p?.accent_color ?? "").trim(),
          }))
          .filter((p) => p.slug);

        const fallbackPrograms = Array.from(programSlugs)
          .sort((a, b) => a.localeCompare(b))
          .map((slug) => ({ slug, title: slug }));

        if (ignore) return;
        setProgramOptions(filteredPrograms.length ? filteredPrograms : fallbackPrograms);
      } catch (e) {
        if (ignore) return;
        setProgramOptions([]);
        setProgramError(
          e?.message ||
            tFn("testsModule.placement.failedLoad", {
              defaultValue: "Failed to load placement tests",
            }),
        );
      } finally {
        if (!ignore) setProgramLoading(false);
      }
    }

    loadProgramsWithTests();

    return () => {
      ignore = true;
    };
  }, [programSlug, tFn]);

  useEffect(() => {
    if (!programSlug) {
      setLoading(false);
      setError("");
      setTests([]);
      return;
    }

    let ignore = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const payload = await programTestsService.fetchProgramTests({
          params: { program: programSlug },
        });
        if (ignore) return;
        setTests(asArray(payload?.tests));
      } catch (e) {
        if (ignore) return;
        setTests([]);
        setError(
          e?.message ||
            tFn("testsModule.placement.failedLoad", {
              defaultValue: "Failed to load placement tests",
            }),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();

    return () => {
      ignore = true;
    };
  }, [programSlug, tFn]);

  const levels = useMemo(() => uniqueLevelsFromTests(tests), [tests]);

  const handlePick = (levelGrade) => {
    const chosen = asArray(tests)
      .filter(
        (t) =>
          String(t?.level_grade ?? "").trim() === String(levelGrade).trim(),
      )
      .sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0))[0];

    if (!chosen?.id) return;

    // Guest-only lead form step.
    if (!accessToken && !lead) {
      navigate(
        `/placement-test/start?program=${encodeURIComponent(programSlug)}&testId=${encodeURIComponent(
          String(chosen.id),
        )}`,
      );
      return;
    }

    navigate(
      `/placement-test/${chosen.id}?program=${encodeURIComponent(programSlug)}`,
    );
  };

  const currentProgram = programSlug
    ? programOptions.find((p) => p.slug === programSlug)
    : null;

  return (
    <main className="placement-level" dir={dir}>
      <div className="pp-shell placement-level__shell">
        <header className="placement-level__header">
          <div>
            <span className="pp-section-tag pp-section-tag--desert">
              {tFn("testsModule.placement.title", {
                defaultValue: "Placement test",
              })}
            </span>
            <h1 className="placement-level__title">
              {!programSlug
                ? tFn("testsModule.placement.chooseProgram", {
                    defaultValue: "Choose a program",
                  })
                : tFn("testsModule.placement.chooseLevel", {
                    defaultValue: "Choose a level",
                  })}
            </h1>
            {programSlug ? (
              <p className="placement-level__program">
                <i
                  className={currentProgram?.icon || "fa-solid fa-book-open"}
                  aria-hidden="true"
                />
                {currentProgram?.title || programSlug}
              </p>
            ) : null}
            <p className="placement-level__subtitle">
              {!programSlug
                ? tFn("testsModule.placement.pickProgramSubtitle", {
                    defaultValue:
                      "Select a program to see the available placement tests.",
                  })
                : tFn("testsModule.placement.pickSubtitle", {
                    defaultValue:
                      "We’ll pick the most recent published test for that level.",
                  })}
            </p>
          </div>
          <div className="placement-level__actions">
            {programSlug ? (
              <Link className="btn ghost" to="/placement-test">
                <i className="fa-solid fa-arrow-left placement-level__back" aria-hidden="true" />{" "}
                {tFn("testsModule.placement.changeProgram", {
                  defaultValue: "Change program",
                })}
              </Link>
            ) : (
              <Link className="btn ghost" to="/">
                {tFn("testsModule.placement.backHome", {
                  defaultValue: "Back to home",
                })}
              </Link>
            )}
          </div>
        </header>

        {!programSlug ? (
          <>
            {programError ? (
              <div className="placement-level__error">{programError}</div>
            ) : null}

            {programLoading ? (
              <div className="placement-level__loading">
                {tFn("testsModule.common.loading", {
                  defaultValue: "Loading...",
                })}
              </div>
            ) : null}

            {!programLoading && !programOptions.length ? (
              <div className="placement-level__empty">
                {tFn("testsModule.placement.noPrograms", {
                  defaultValue:
                    "No programs with published placement tests found.",
                })}
              </div>
            ) : null}

            <div className="placement-level__grid" role="list">
              {programOptions.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  className="placement-level__card"
                  onClick={() =>
                    navigate(
                      `/placement-test?program=${encodeURIComponent(p.slug)}`,
                    )
                  }
                  role="listitem"
                  style={p.accent ? { "--program-accent": p.accent } : undefined}
                >
                  <span className="placement-level__icon" aria-hidden="true">
                    <i className={p.icon || "fa-solid fa-book-open"} />
                  </span>
                  <div className="placement-level__cardValue">
                    {p.title || p.slug}
                  </div>
                  {p.description ? (
                    <p className="placement-level__cardDesc">{p.description}</p>
                  ) : null}
                  <div className="placement-level__cardLabel">
                    {tFn("testsModule.placement.chooseLevelCta", {
                      defaultValue: "Choose level",
                    })}
                    <i className="fa-solid fa-arrow-right placement-level__arrow" aria-hidden="true" />
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            {error ? <div className="placement-level__error">{error}</div> : null}
            {loading ? (
              <div className="placement-level__loading">
                {tFn("testsModule.common.loading", { defaultValue: "Loading..." })}
              </div>
            ) : null}

            {!loading && !levels.length ? (
              <div className="placement-level__empty">
                {tFn("testsModule.placement.noPublished", {
                  defaultValue:
                    "No published placement tests found for this program.",
                })}
              </div>
            ) : null}

            <div className="placement-level__grid" role="list">
              {levels.map((lvl) => (
                <button
                  key={lvl}
                  type="button"
                  className="placement-level__card"
                  onClick={() => handlePick(lvl)}
                  role="listitem"
                >
                  <span className="placement-level__icon" aria-hidden="true">
                    <i className="fa-solid fa-layer-group" />
                  </span>
                  <div className="placement-level__cardValue">{lvl}</div>
                  <div className="placement-level__cardLabel">
                    {tFn("testsModule.placement.start", { defaultValue: "Start" })}
                    <i className="fa-solid fa-arrow-right placement-level__arrow" aria-hidden="true" />
                  </div>
                </button>
              ))}
            </div>

            {!accessToken ? (
              <>
                <p className="placement-level__note">
                  {!lead
                    ? tFn("testsModule.placement.noteNoLead", {
                        defaultValue:
                          "Pick a level, then fill the child/contact form before starting the test.",
                      })
                    : tFn("testsModule.placement.noteLead", {
                        child: lead.child_full_name || "",
                        parent: lead.parent_full_name || "",
                        defaultValue: `Child: ${lead.child_full_name || ""} — Parent: ${lead.parent_full_name || ""}`,
                      })}
                </p>

                {lead ? (
                  <div
                    className="placement-level__actions"
                    style={{ marginTop: "0.75rem" }}
                  >
                    <Link
                      className="btn ghost"
                      to={`/placement-test/start?program=${encodeURIComponent(programSlug)}`}
                    >
                      {tFn("testsModule.placement.editInfo", {
                        defaultValue: "Edit info",
                      })}
                    </Link>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
}
